import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CalendarCheck, Loader2, Check, ArrowLeft, Sparkles, LogIn, UserCircle2 } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { TextReveal } from '@/components/TextReveal';
import { SEO } from '@/components/SEO';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import { useAuth } from '@/context/AuthContext';
import { getServices, getPublicPackages, getPhotographerProfile, getBookingAvailability, createInstantBooking } from '@/lib/api';
import { VerifiedBadge } from '@/components/marketplace/VerifiedBadge';
import { accentStyle } from '@/lib/studioAccent';
import { parseIsoDate, formatIsoDate } from '@/lib/dateUtils';
import { BOOK_WITH_STRIP } from '@/constants/testIds';
import '@/styles/studioAccent.css';
import { formatCents } from '@/lib/money';
import { SITE_IMAGES, SITE_VIDEOS } from '@/lib/siteImages';
import { BOOK_PAGE } from '@/constants/testIds';

const ANY = '__any__';
const EMPTY = { name: '', email: '', phone: '', service_slug: ANY, package_id: ANY, session_time: '', location: '', notes: '' };

const fieldClass = 'mt-1.5 bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-11 focus-visible:ring-[hsl(var(--lxra-gold))]';
const labelClass = 'text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]';
const selectContent = 'bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]';

function validate(form, selectedDate, isClient) {
  const errors = {};
  if (!isClient) {
    if (!form.name.trim()) errors.name = 'Please enter your name.';
    if (!form.email.trim()) errors.email = 'Please enter your email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Please enter a valid email address.';
  }
  if (!selectedDate) errors.date = 'Please pick an open date on the calendar.';
  return errors;
}

export default function BookRequest() {
  const { user, isClient } = useAuth();
  const [params] = useSearchParams();
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    service_slug: params.get('service') || ANY,
    package_id: params.get('package') || ANY,
  }));
  const [selectedDate, setSelectedDate] = useState(null); // ISO string YYYY-MM-DD
  const [unavailable, setUnavailable] = useState(null); // null = loading
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success
  const [result, setResult] = useState(null);
  // Marketplace: ?photographer=<handle> routes the booking to that studio.
  const photographerHandle = params.get('photographer') || null;
  const [photographer, setPhotographer] = useState(null);

  useEffect(() => {
    getServices().then(setServices).catch(() => setServices([]));
    getPublicPackages(undefined, photographerHandle).then(setPackages).catch(() => setPackages([]));
    if (photographerHandle) getPhotographerProfile(photographerHandle).then(setPhotographer).catch(() => setPhotographer(null));
    else setPhotographer(null);
  }, [photographerHandle]);

  useEffect(() => {
    setUnavailable(null);
    setSelectedDate(null);
    getBookingAvailability(photographerHandle, 6)
      .then((d) => setUnavailable(d.unavailable_dates || []))
      .catch(() => setUnavailable([]));
  }, [photographerHandle]);

  // If a package was preselected via ?package= but no service, infer the service.
  useEffect(() => {
    if (form.service_slug === ANY && form.package_id !== ANY) {
      const pkg = packages.find((p) => p.id === form.package_id);
      if (pkg?.service_slug) setForm((f) => ({ ...f, service_slug: pkg.service_slug }));
    }
  }, [packages, form.package_id, form.service_slug]);

  const visiblePackages = useMemo(
    () => packages.filter((p) => form.service_slug === ANY || !p.service_slug || p.service_slug === form.service_slug),
    [packages, form.service_slug],
  );
  const selectedPackage = packages.find((p) => p.id === form.package_id);
  const unavailableDateObjs = useMemo(() => (unavailable || []).map(parseIsoDate).filter(Boolean), [unavailable]);
  const selectedDateObj = useMemo(() => (selectedDate ? parseIsoDate(selectedDate) : undefined), [selectedDate]);

  const set = (key) => (value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleService = (slug) => {
    setForm((f) => {
      const pkg = packages.find((p) => p.id === f.package_id);
      const keep = pkg && (slug === ANY || !pkg.service_slug || pkg.service_slug === slug);
      return { ...f, service_slug: slug, package_id: keep ? f.package_id : ANY };
    });
  };

  const handlePickDate = (date) => {
    setSelectedDate(date ? formatIsoDate(date) : null);
    if (errors.date) setErrors((e) => ({ ...e, date: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate(form, selectedDate, isClient);
    setErrors(v);
    if (Object.keys(v).length) return;
    setStatus('loading');
    try {
      const res = await createInstantBooking({
        name: isClient ? undefined : form.name.trim(),
        email: isClient ? undefined : form.email.trim(),
        phone: form.phone.trim(),
        service_slug: form.service_slug === ANY ? null : form.service_slug,
        package_id: form.package_id === ANY ? null : form.package_id,
        session_date: selectedDate,
        session_time: form.session_time.trim(),
        location: form.location.trim(),
        notes: form.notes.trim(),
        studio_handle: photographerHandle,
      });
      setResult(res);
      setStatus('success');
    } catch (err) {
      setStatus('idle');
      const detail = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(detail);
      if (err?.response?.status === 409) {
        // The date was just taken — refresh the calendar so it's accurate.
        setSelectedDate(null);
        getBookingAvailability(photographerHandle, 6).then((d) => setUnavailable(d.unavailable_dates || [])).catch(() => {});
      }
    }
  };

  return (
    <div className="pt-16" data-testid={BOOK_PAGE.root}>
      <SEO title="Book Your Session" description="Pick an open date and book your photography or videography session with LXRA Studios in the DMV area — instantly, no waiting for confirmation." />
      <section className="relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-4rem)]">
          {/* FORM SIDE */}
          <div className="flex items-center bg-[#0A0A0A] px-4 sm:px-8 lg:px-16 py-20">
            <Reveal className="w-full max-w-lg">
              {photographerHandle && (
                <div
                  data-testid={BOOK_WITH_STRIP.root}
                  data-studio-profile="true"
                  style={accentStyle(photographer?.accent_color)}
                  className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] px-4 py-3 border-l-2 studio-accent-border-strong"
                >
                  <span className="h-2 w-2 rounded-full studio-accent-bg" />
                  <p className="text-sm text-[hsl(var(--lxra-fg))]">
                    Booking with <span data-testid={BOOK_WITH_STRIP.name} className="font-medium">{photographer?.name || photographerHandle}</span>
                  </p>
                  {photographer?.verified && <VerifiedBadge />}
                  <Link to="/photographers" data-testid={BOOK_WITH_STRIP.change} className="ml-auto text-[11px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))]">Change</Link>
                </div>
              )}
              <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))] mb-3">Book Your Session</p>
              <h1 className="font-display text-4xl sm:text-5xl tracking-[0.03em] leading-[0.98] text-[hsl(var(--lxra-fg))]">
                <TextReveal text="PICK YOUR" />
                <br />
                <TextReveal text="DATE." delay={0.08} />
              </h1>
              <div className="mt-5 h-[2px] w-16 bg-[hsl(var(--lxra-gold))]" />
              <p className="mt-6 text-sm sm:text-base text-[hsl(var(--lxra-muted))] leading-relaxed max-w-md">
                {photographer
                  ? <>Pick any open date below to lock it in with {photographer.name} right now — no waiting on a confirmation email.</>
                  : <>Pick any open date below to lock it in right now — no waiting on a confirmation email.</>}
              </p>

              {status === 'success' ? (
                <div data-testid={BOOK_PAGE.success} className="mt-10 rounded-2xl border border-[hsl(var(--lxra-gold))]/40 bg-[#0F0F10] p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--lxra-gold))] text-[#0A0A0A]"><Check className="h-6 w-6" /></span>
                  <h2 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-5">You&apos;re booked!</h2>
                  <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">{result?.message}</p>
                  {isClient ? (
                    <Link to="/client" className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold-hover))]">
                      <UserCircle2 className="h-3.5 w-3.5" /> View My Bookings
                    </Link>
                  ) : (
                    <p className="mt-4 text-xs text-[hsl(var(--lxra-muted))]">
                      Want to get a head start? <Link to="/plan" className="text-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold-hover))] inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> Create your Creative Blueprint</Link>
                    </p>
                  )}
                  <Link to="/" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-fg))] hover:text-[hsl(var(--lxra-gold))] transition-colors">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to home
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid={BOOK_PAGE.form} className="mt-8 space-y-5" noValidate>
                  {isClient ? (
                    <div data-testid={BOOK_PAGE.loggedInNotice} className="flex items-center gap-2.5 rounded-lg border border-[hsl(var(--lxra-gold))]/25 bg-[hsl(var(--lxra-gold))]/5 px-3.5 py-2.5 text-xs text-[hsl(var(--lxra-muted))]">
                      <UserCircle2 className="h-4 w-4 text-[hsl(var(--lxra-gold))] flex-shrink-0" />
                      Booking as <span className="text-[hsl(var(--lxra-fg))] font-medium">{user?.name || user?.email}</span> — this will be saved to your account.
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="book-name" className={labelClass}>Your Name</Label>
                          <Input id="book-name" value={form.name} onChange={(e) => set('name')(e.target.value)} data-testid={BOOK_PAGE.nameInput} className={fieldClass} aria-invalid={Boolean(errors.name)} />
                          {errors.name && <p className="mt-1 text-xs text-[hsl(8,55%,68%)]" data-testid={BOOK_PAGE.errorText}>{errors.name}</p>}
                        </div>
                        <div>
                          <Label htmlFor="book-email" className={labelClass}>Email</Label>
                          <Input id="book-email" type="email" value={form.email} onChange={(e) => set('email')(e.target.value)} data-testid={BOOK_PAGE.emailInput} className={fieldClass} aria-invalid={Boolean(errors.email)} />
                          {errors.email && <p className="mt-1 text-xs text-[hsl(8,55%,68%)]">{errors.email}</p>}
                        </div>
                      </div>
                      <p data-testid={BOOK_PAGE.loginHint} className="text-xs text-[hsl(var(--lxra-muted))]">
                        Have an account? <Link to="/login" className="text-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold-hover))] inline-flex items-center gap-1"><LogIn className="h-3 w-3" /> Log in</Link> to save this booking and track it later.
                      </p>
                    </>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="book-phone" className={labelClass}>Phone (optional)</Label>
                      <Input id="book-phone" value={form.phone} onChange={(e) => set('phone')(e.target.value)} data-testid={BOOK_PAGE.phoneInput} className={fieldClass} />
                    </div>
                    <div>
                      <Label className={labelClass}>Service</Label>
                      <Select value={form.service_slug} onValueChange={handleService}>
                        <SelectTrigger data-testid={BOOK_PAGE.serviceSelect} className={fieldClass}><SelectValue /></SelectTrigger>
                        <SelectContent className={selectContent}>
                          <SelectItem value={ANY}>Not sure yet</SelectItem>
                          {services.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className={labelClass}>Package</Label>
                    <Select value={form.package_id} onValueChange={set('package_id')}>
                      <SelectTrigger data-testid={BOOK_PAGE.packageSelect} className={fieldClass}><SelectValue /></SelectTrigger>
                      <SelectContent className={selectContent}>
                        <SelectItem value={ANY}>Help me choose</SelectItem>
                        {visiblePackages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatCents(p.price_cents)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {selectedPackage && (
                      <div data-testid={BOOK_PAGE.packageSummary} className="mt-2 rounded-lg border border-[hsl(var(--lxra-gold))]/30 bg-[#0F0F10] px-3 py-2 text-xs text-[hsl(var(--lxra-muted))]">
                        <span className="text-[hsl(var(--lxra-fg))]">{selectedPackage.name}</span> · {formatCents(selectedPackage.price_cents)}
                        {selectedPackage.duration_label ? ` · ${selectedPackage.duration_label}` : ''}
                        {selectedPackage.deposit_cents ? ` · ${formatCents(selectedPackage.deposit_cents)} retainer due separately` : ''}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className={labelClass}>Pick an open date</Label>
                    {unavailable === null ? (
                      <div className="mt-1.5 h-[300px] rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416] animate-pulse" />
                    ) : (
                      <Calendar
                        mode="single"
                        selected={selectedDateObj}
                        onSelect={handlePickDate}
                        disabled={[{ before: new Date() }, ...unavailableDateObjs]}
                        fromMonth={new Date()}
                        numberOfMonths={1}
                        data-testid={BOOK_PAGE.calendar}
                        className="mt-1.5 rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416] mx-auto sm:mx-0 w-fit"
                      />
                    )}
                    {errors.date && <p className="mt-1.5 text-xs text-[hsl(8,55%,68%)]" data-testid={BOOK_PAGE.noDateHint}>{errors.date}</p>}
                    {selectedDate && (
                      <p className="mt-2 text-xs text-[hsl(var(--lxra-gold))] flex items-center gap-1.5">
                        <CalendarCheck className="h-3.5 w-3.5" /> {parseIsoDate(selectedDate).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="book-time" className={labelClass}>Preferred time (optional)</Label>
                    <Input id="book-time" value={form.session_time} onChange={(e) => set('session_time')(e.target.value)} data-testid={BOOK_PAGE.timeInput} className={fieldClass} placeholder="Golden hour, 10am, etc." />
                  </div>
                  <div>
                    <Label htmlFor="book-location" className={labelClass}>Location idea (optional)</Label>
                    <Input id="book-location" value={form.location} onChange={(e) => set('location')(e.target.value)} data-testid={BOOK_PAGE.locationInput} className={fieldClass} placeholder="Georgetown Waterfront, National Mall, your venue…" />
                  </div>
                  <div>
                    <Label htmlFor="book-notes" className={labelClass}>Anything else?</Label>
                    <Textarea id="book-notes" rows={4} value={form.notes} onChange={(e) => set('notes')(e.target.value)} data-testid={BOOK_PAGE.notesTextarea} className={`${fieldClass} h-auto resize-none`} placeholder="Occasion, number of people, must-have shots…" />
                  </div>
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    data-testid={BOOK_PAGE.submitButton}
                    className="inline-flex items-center justify-center w-full sm:w-auto h-12 px-8 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] transition-colors text-sm uppercase tracking-[0.1em] font-semibold disabled:opacity-60"
                  >
                    {status === 'loading' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CalendarCheck className="h-4 w-4 mr-2" />}
                    Book This Date
                  </button>
                </form>
              )}
            </Reveal>
          </div>

          {/* MEDIA SIDE */}
          <div className="relative min-h-[40vh] lg:min-h-full overflow-hidden lxra-grain">
            <video className="absolute inset-0 h-full w-full object-cover" src={SITE_VIDEOS.contactBackground} poster={SITE_IMAGES.contactBackground} autoPlay loop muted playsInline aria-hidden="true" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-14">
              <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))] mb-3">How it works</p>
              <ol className="space-y-3 text-sm text-[hsl(var(--lxra-fg))] max-w-md">
                <li className="flex gap-3"><span className="font-display text-[hsl(var(--lxra-gold))]">01</span> Pick an open date on the calendar — it locks in instantly.</li>
                <li className="flex gap-3"><span className="font-display text-[hsl(var(--lxra-gold))]">02</span> We&apos;ll follow up about the retainer to secure your session.</li>
                <li className="flex gap-3"><span className="font-display text-[hsl(var(--lxra-gold))]">03</span> Your private gallery arrives 7–10 days after the shoot.</li>
              </ol>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
